import React from 'react';

// UIKit Components
import { IconButton } from '@spazious/react-components';
// Icons
import { IconAddCircle, IconRemoveCircle } from '@spazious/react-icons';
// Theming
import { styled } from '@spazious/react-styles';

export type QuantityBoxValueChangeEvent =
  | React.ChangeEvent<HTMLInputElement>
  | React.MouseEvent<HTMLButtonElement, MouseEvent>;

export interface QuantityBoxProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onBlur'> {
  onChange?: (evt: QuantityBoxValueChangeEvent, value: number, name: string) => void;
  onBlur?: (evt: React.FocusEvent<HTMLInputElement, Element>, value: number) => void;
  value?: number;
  name?: string;
  maxValue?: number;
  minValue?: number;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  error?: boolean;
  inputWidth?: string;
}

const StyledIconButton = styled(IconButton)`
  color: var(--secondary-color);
  padding: 6px;

  display: flex;
  align-items: center;
  justify-content: center;

  &&:not(:disabled):hover {
    background-color: var(--secondary-color-muted) !important;
    opacity: 1 !important;
    box-shadow: none !important;
    border-radius: 50%;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const QuantityBox = React.forwardRef<HTMLDivElement, QuantityBoxProps>(
  (
    {
      onBlur,
      onChange,
      value = 0,
      name,
      disabled = false,
      maxValue = Infinity,
      minValue = 0,
      ...restProps
    },
    ref
  ) => {
    const isMinValueInteger = Number.isInteger(minValue);
    const isMaxValueInteger = Number.isInteger(maxValue);
    const hasMinOrMaxValue = isMinValueInteger || isMaxValueInteger;
    const onlyNumbersRegExp = /^\d+$/;

    const updateValue = (evt: QuantityBoxValueChangeEvent, value: number) => {
      onChange?.(evt, value, name);
    };

    const handleOnClickMinus: React.MouseEventHandler<HTMLButtonElement> = (evt) => {
      updateValue(evt, +value - 1);
    };

    const handleOnClickPlus: React.MouseEventHandler<HTMLButtonElement> = (evt) => {
      updateValue(evt, +value + 1);
    };

    const handleOnChangeInput: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
      const inputValue = evt.target.value;

      if (onlyNumbersRegExp.test(inputValue)) {
        if (hasMinOrMaxValue) {
          // if min value is more than default input value
          if (+inputValue < minValue) {
            updateValue(evt, minValue);
          } else if (+inputValue <= +maxValue && +inputValue >= +minValue) {
            updateValue(evt, +inputValue);
          }
        } else {
          updateValue(evt, +inputValue);
        }
      } else if (!inputValue) {
        updateValue(evt, 0);
      }
    };

    // Handle the blur event, but only process it for input elements
    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (evt) => {
      onBlur?.(evt, +evt.target.value);
    };

    return (
      <div ref={ref} {...restProps}>
        <StyledIconButton
          disabled={disabled || !+value || (isMinValueInteger && +value <= minValue)}
          onClick={handleOnClickMinus}
        >
          <IconRemoveCircle />
        </StyledIconButton>
        <input
          disabled={disabled}
          name={name}
          value={value}
          onBlur={handleBlur}
          onChange={handleOnChangeInput}
        />
        <StyledIconButton
          disabled={disabled || (isMaxValueInteger && +value >= maxValue)}
          onClick={handleOnClickPlus}
        >
          <IconAddCircle />
        </StyledIconButton>
      </div>
    );
  }
);

export default styled(QuantityBox).withConfig({
  shouldForwardProp: (prop) => !['size', 'error', 'inputWidth'].includes(prop),
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  & > input {
    padding: 6px;
    width: calc(${({ inputWidth = '4ch' }) => inputWidth} + 12px); // 12px padding
    box-shadow: 0 0 1px 1px var(--light-gray);
    border: 0;
    border-radius: 6px;
    font-size: 16px;
    text-align: center;
    background: transparent;
    color: var(--primary-text);
    transition: all 0.2s;
  }

  &:focus-within {
    & > input {
      box-shadow: 0 0 1px 1px var(--primary-color);
    }
  }

  ${({ size = 'medium' }) =>
    ({
      small: `
    & > input {
      padding: 2px 6px;
    }
    `,
      large: `
    & > input {
      padding: 8px 12px;
    }
    `,
      medium: `
    & > input {
      padding: 6px 10px;
    }
    `,
    }[size])}

  ${({ error }) =>
    error &&
    `
    & > input {
      box-shadow: 0 0 1px 1px var(--red-color);
      }
    `}
`;
